import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { IAuthenticatedUser } from '../../pages/Requisitions/constants/requisition.interface';
import { baseQueryWithReauth } from './baseQuery';

interface ILoginResponse {
  user: IAuthenticatedUser;
  chatbotToken?: string;
  assetToken?: string;
}
interface ISendOtpResponse {
  ID: number;
  FullName: string;
  EmailID: string;
}

interface IVerifyOtpResponse {
  user: IAuthenticatedUser;
  chatbotToken?: string;
  assetToken?: string;
}

export type AuthenticationMethod =
  | 'LEGACY_PASSWORD'
  | 'LEGACY_OTP'
  | 'KEYCLOAK';

export interface IAuthenticationPolicy {
  ssoEnabled: boolean;
  captchaEnabled: boolean;
}

export interface IActiveSession {
  sessionId: string;
  deviceLabel: string;
  userAgent: string;
  ipAddress: string;
  loginAt: string;
  lastActiveAt: string;
  isCurrent?: boolean;
}

export interface ISessionLimitError {
  message: string;
  statusCode: number;
  errorCode: 'SESSION_LIMIT_REACHED';
  data: {
    activeSessions: IActiveSession[];
    maxSessions: number;
  };
}

const authApi = createApi({
  reducerPath: 'auth',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['User', 'UserProfile', 'Sessions'],
  endpoints: builder => ({
    getAuthConfiguration: builder.query<
      BackendResponse<IAuthenticationPolicy>,
      void
    >({
      query: () => '/auth/config',
    }),
    login: builder.mutation<
      BackendResponse<ILoginResponse>,
      { email: string; password: string; captchaToken?: string }
    >({
      query: payload => ({
        url: '/auth/login',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['User']),
    }),
    checkSession: builder.query<
      BackendResponse<{
        isAuthenticated: boolean;
        user: IAuthenticatedUser;
        chatbotToken?: string;
        assetToken?: string;
        authenticationMethod?: AuthenticationMethod;
      }>,
      void
    >({
      query: () => '/auth/check-session',
      providesTags: ['User'],
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
      invalidatesTags: ['User'],
    }),
    forgotPassword: builder.mutation<void, { email: string; captchaToken?: string }>({
      query: payload => ({
        url: '/auth/forgot-password',
        method: 'POST',
        body: payload,
      }),
    }),
    resetPassword: builder.mutation<void, any>({
      query: payload => ({
        url: '/auth/reset-password',
        method: 'POST',
        body: payload,
      }),
    }),
    getUserDetails: builder.query<BackendResponse<any>, { token: string }>({
      query: ({ token }) => ({
        url: `/auth/user-detail/${token}`,
        method: 'GET',
      }),
    }),
    verifyingUser: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: '/auth/verification',
        method: 'POST',
        body: payload,
      }),
    }),
    changePassword: builder.mutation<void, any>({
      query: payload => ({
        url: '/auth/change-password',
        method: 'PUT',
        body: payload,
      }),
    }),
    uploadProfilePicture: builder.mutation<
      BackendResponse<{ profilePicUrl: string }>,
      { id: number | string; formData: FormData }
    >({
      query: ({ id, formData }) => ({
        url: `/users/${id}/profile-pic`,
        method: 'PUT',
        body: formData,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'UserProfile', id }],
    }),
    getProfilePicture: builder.query<Blob, number | string>({
      query: id => ({
        url: `/users/${id}/profile-pic`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
      providesTags: (__, _, id) => [{ type: 'UserProfile', id }],
    }),
    // ✅ OTP Login - Step 1: Send OTP
    sendOtp: builder.mutation<
      BackendResponse<{ data: ISendOtpResponse; message: string }>,
      { email: string; captchaToken?: string }
    >({
      query: payload => ({
        url: '/auth/send-otp',
        method: 'POST',
        body: payload,
      }),
    }),
    // ✅ OTP Login - Step 2: Verify OTP
    verifyOtp: builder.mutation<
      BackendResponse<IVerifyOtpResponse>,
      { email: string; otp: string; captchaToken?: string }
    >({
      query: payload => ({
        url: '/auth/verify-otp',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['User'],
    }),
    // ✅ Session Management - Get active sessions
    getActiveSessions: builder.query<
      BackendResponse<{
        sessions: IActiveSession[];
        maxSessions: number;
      }>,
      void
    >({
      query: () => '/auth/sessions',
      providesTags: ['Sessions'],
    }),
    // ✅ Session Management - Force logout a specific session (authenticated — used from settings)
    logoutSession: builder.mutation<BackendResponse<{ message: string }>, string>({
      query: (sessionId) => ({
        url: `/auth/sessions/${sessionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Sessions'],
    }),
    // ✅ Public sessions list — used from device-limit page to refresh without a session cookie
    publicGetSessions: builder.mutation<
      BackendResponse<{ activeSessions: IActiveSession[]; maxSessions: number }>,
      { email: string; password?: string }
    >({
      query: (body) => ({
        url: '/auth/sessions/list',
        method: 'POST',
        body,
      }),
    }),
    // ✅ Public force-logout — used from device-limit page (no session cookie available)
    publicLogoutSession: builder.mutation<
      BackendResponse<{ message: string }>,
      { email: string; password?: string; sessionId: string }
    >({
      query: (body) => ({
        url: '/auth/sessions/force-logout',
        method: 'POST',
        body,
      }),
    }),
    getPendingSsoSessions: builder.mutation<
      BackendResponse<{ activeSessions: IActiveSession[]; maxSessions: number }>,
      void
    >({
      query: () => '/auth/sso/pending/sessions',
    }),
    completePendingSsoLogin: builder.mutation<
      BackendResponse<{ completed: boolean; redirectUrl?: string }>,
      void
    >({
      query: () => ({
        url: '/auth/sso/pending/complete',
        method: 'POST',
      }),
    }),
    replacePendingSsoSession: builder.mutation<
      BackendResponse<{ completed: boolean; redirectUrl?: string }>,
      string
    >({
      query: (sessionId) => ({
        url: `/auth/sso/pending/sessions/${sessionId}/replace`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetAuthConfigurationQuery,
  useLoginMutation,
  useLogoutMutation,
  useCheckSessionQuery,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useVerifyingUserMutation,
  useGetUserDetailsQuery,
  useChangePasswordMutation,
  useUploadProfilePictureMutation,
  useGetProfilePictureQuery,
  useLazyGetProfilePictureQuery,
  useSendOtpMutation,
  useVerifyOtpMutation,
  useGetActiveSessionsQuery,
  useLogoutSessionMutation,
  usePublicLogoutSessionMutation,
  usePublicGetSessionsMutation,
  useGetPendingSsoSessionsMutation,
  useCompletePendingSsoLoginMutation,
  useReplacePendingSsoSessionMutation,
} = authApi;

export default authApi;
